/**
 * Effect Sprite Renderer (L4-EFFECT-006)
 *
 * Renders ability effects using sprites from the Effect Gallery.
 * Uses delivery-specific animation paths (arc, cone, beam, etc.).
 */

import * as PIXI from 'pixi.js';

export type DeliveryType = 'melee' | 'projectile' | 'breath' | 'beam' | 'aoe' | 'buff' | 'strike';

/**
 * Cached effect sprite data
 */
export interface CachedEffectSprite {
  key: string;
  element: string;
  effectType: string;
  textures: PIXI.Texture[];
  frameCount: number;
}

/**
 * Effect sprite cache - loaded from backend API
 */
const effectCache = new Map<string, CachedEffectSprite>();

/**
 * Load effects from base64 frames into the cache
 */
export async function loadEffectSprites(effects: Record<string, {
  key: string;
  element: string;
  effectType: string;
  frames: string[];
  frameCount: number;
}>): Promise<void> {
  for (const [key, effect] of Object.entries(effects)) {
    if (effectCache.has(key)) {
      continue; // Already loaded
    }

    try {
      const textures: PIXI.Texture[] = [];

      for (const frame of effect.frames) {
        // Convert base64 to texture
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Failed to load effect frame'));
          img.src = `data:image/png;base64,${frame}`;
        });

        const texture = PIXI.Texture.from(img);
        textures.push(texture);
      }

      effectCache.set(key, {
        key: effect.key,
        element: effect.element,
        effectType: effect.effectType,
        textures,
        frameCount: effect.frameCount
      });

      console.log(`[EffectSprites] Loaded ${key} (${textures.length} frames)`);
    } catch (error) {
      console.error(`[EffectSprites] Failed to load ${key}:`, error);
    }
  }
}

/**
 * Get cached effect sprite
 */
export function getCachedEffect(key: string): CachedEffectSprite | undefined {
  return effectCache.get(key);
}

/**
 * Check if an effect is cached
 */
export function hasEffect(key: string): boolean {
  return effectCache.has(key);
}

/**
 * Clear the effect cache
 */
export function clearEffectCache(): void {
  // Destroy textures
  for (const effect of effectCache.values()) {
    for (const texture of effect.textures) {
      texture.destroy(true);
    }
  }
  effectCache.clear();
  console.log('[EffectSprites] Cache cleared');
}

/**
 * Effect render request
 */
export interface SpriteEffectRequest {
  effectKey: string;
  delivery: DeliveryType;
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;
  aoeRadius?: number;
  strikeOrigin?: 'sky' | 'ground';
  onComplete?: () => void;
}

/**
 * Render a sprite-based effect with delivery-specific animation
 */
export function renderSpriteEffect(
  container: PIXI.Container,
  request: SpriteEffectRequest
): boolean {
  const effect = effectCache.get(request.effectKey);
  if (!effect || effect.textures.length === 0) {
    console.warn(`[EffectSprites] Effect not found: ${request.effectKey}`);
    return false;
  }

  switch (request.delivery) {
    case 'projectile':
      renderArcProjectile(container, effect, request);
      break;
    case 'breath':
      renderBreathCone(container, effect, request);
      break;
    case 'beam':
      renderBeamLine(container, effect, request);
      break;
    case 'aoe':
      renderAOERing(container, effect, request);
      break;
    case 'strike':
      renderStrikeEffect(container, effect, request);
      break;
    case 'buff':
      renderBuffAura(container, effect, request);
      break;
    case 'melee':
    default:
      renderMeleeSlash(container, effect, request);
      break;
  }

  return true;
}

/**
 * Create animated sprite from effect
 */
function createAnimatedSprite(effect: CachedEffectSprite, animationSpeed = 0.2): PIXI.AnimatedSprite {
  const sprite = new PIXI.AnimatedSprite(effect.textures);
  sprite.animationSpeed = animationSpeed;
  sprite.anchor.set(0.5);
  sprite.play();
  return sprite;
}

/**
 * Render projectile with arc trajectory
 */
function renderArcProjectile(
  container: PIXI.Container,
  effect: CachedEffectSprite,
  request: SpriteEffectRequest
): void {
  const sprite = createAnimatedSprite(effect, 0.3);
  sprite.position.set(request.startX, request.startY);
  container.addChild(sprite);

  const dx = request.targetX - request.startX;
  const dy = request.targetY - request.startY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const duration = Math.max(300, distance * 2); // ms
  const startTime = performance.now();

  // Arc height - higher for longer distances
  const arcHeight = Math.min(distance * 0.3, 100);

  // Rotate to face direction
  sprite.rotation = Math.atan2(dy, dx);

  const animate = () => {
    const elapsed = performance.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // Linear interpolation for X
    sprite.x = request.startX + dx * progress;

    // Parabolic arc for Y
    const arcOffset = arcHeight * Math.sin(progress * Math.PI);
    sprite.y = request.startY + dy * progress - arcOffset;

    // Slight rotation animation
    sprite.rotation = Math.atan2(dy - arcOffset * 2 * (0.5 - progress), dx);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(sprite);
      sprite.destroy();
      request.onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render breath cone expanding from caster
 */
function renderBreathCone(
  container: PIXI.Container,
  effect: CachedEffectSprite,
  request: SpriteEffectRequest
): void {
  const sprite = createAnimatedSprite(effect, 0.2);
  sprite.position.set(request.startX, request.startY);
  container.addChild(sprite);

  const dx = request.targetX - request.startX;
  const dy = request.targetY - request.startY;
  const angle = Math.atan2(dy, dx);
  sprite.rotation = angle;

  let frame = 0;
  const totalFrames = 30;

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    // Expand scale over time
    const scale = 0.5 + progress * 1.5;
    sprite.scale.set(scale, scale * 0.7); // Wider than tall for cone shape

    // Move forward
    sprite.x = request.startX + dx * progress * 0.6;
    sprite.y = request.startY + dy * progress * 0.6;

    // Fade out in last 30%
    sprite.alpha = progress > 0.7 ? 1 - (progress - 0.7) / 0.3 : 1;

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(sprite);
      sprite.destroy();
      request.onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render beam as line of sprites
 */
function renderBeamLine(
  container: PIXI.Container,
  effect: CachedEffectSprite,
  request: SpriteEffectRequest
): void {
  const sprite = createAnimatedSprite(effect, 0.4);
  sprite.position.set(
    (request.startX + request.targetX) / 2,
    (request.startY + request.targetY) / 2
  );
  container.addChild(sprite);

  const dx = request.targetX - request.startX;
  const dy = request.targetY - request.startY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  const angle = Math.atan2(dy, dx);

  sprite.rotation = angle;
  // Scale to span the distance
  sprite.scale.set(distance / 64, 1);

  let frame = 0;
  const totalFrames = 15;

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    // Flash effect
    const intensity = frame < 3 ? 1 + frame * 0.3 : 1.9 * (1 - progress);
    sprite.alpha = Math.min(1, intensity);
    sprite.scale.y = 1 + (frame < 3 ? frame * 0.3 : 0);

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(sprite);
      sprite.destroy();
      request.onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render AOE ring expanding at target
 */
function renderAOERing(
  container: PIXI.Container,
  effect: CachedEffectSprite,
  request: SpriteEffectRequest
): void {
  const sprite = createAnimatedSprite(effect, 0.25);
  sprite.position.set(request.targetX, request.targetY);
  sprite.scale.set(0.3);
  container.addChild(sprite);

  const targetScale = (request.aoeRadius || 60) / 32;
  let frame = 0;
  const totalFrames = 25;

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    // Expand
    const scale = 0.3 + (targetScale - 0.3) * Math.min(progress * 1.5, 1);
    sprite.scale.set(scale);

    // Fade out
    sprite.alpha = progress < 0.4 ? 1 : 1 - (progress - 0.4) / 0.6;

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(sprite);
      sprite.destroy();
      request.onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render strike effect (falling from sky or rising from ground)
 */
function renderStrikeEffect(
  container: PIXI.Container,
  effect: CachedEffectSprite,
  request: SpriteEffectRequest
): void {
  const sprite = createAnimatedSprite(effect, 0.3);
  sprite.position.set(request.targetX, request.targetY);
  container.addChild(sprite);

  const isFromSky = request.strikeOrigin !== 'ground';
  const startOffset = isFromSky ? -150 : 150;
  sprite.y = request.targetY + startOffset;

  let frame = 0;
  const totalFrames = 20;

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    // Move toward target
    const easeOut = 1 - Math.pow(1 - progress, 3); // Ease-out cubic
    sprite.y = request.targetY + startOffset * (1 - easeOut);

    // Grow slightly on impact
    if (progress > 0.8) {
      const impactProgress = (progress - 0.8) / 0.2;
      sprite.scale.set(1 + impactProgress * 0.3);
      sprite.alpha = 1 - impactProgress;
    }

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(sprite);
      sprite.destroy();
      request.onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render buff aura around target
 */
function renderBuffAura(
  container: PIXI.Container,
  effect: CachedEffectSprite,
  request: SpriteEffectRequest
): void {
  const sprite = createAnimatedSprite(effect, 0.15);
  sprite.position.set(request.targetX, request.targetY);
  sprite.scale.set(0.5);
  sprite.alpha = 0;
  container.addChild(sprite);

  let frame = 0;
  const totalFrames = 45;

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    // Pulse effect
    const pulse = Math.sin(frame * 0.3) * 0.1;
    sprite.scale.set(0.8 + pulse);

    // Fade in, hold, fade out
    if (progress < 0.2) {
      sprite.alpha = progress / 0.2;
    } else if (progress > 0.7) {
      sprite.alpha = 1 - (progress - 0.7) / 0.3;
    } else {
      sprite.alpha = 1;
    }

    // Slight float upward
    sprite.y = request.targetY - progress * 20;

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(sprite);
      sprite.destroy();
      request.onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render melee slash at target
 */
function renderMeleeSlash(
  container: PIXI.Container,
  effect: CachedEffectSprite,
  request: SpriteEffectRequest
): void {
  const sprite = createAnimatedSprite(effect, 0.4);
  sprite.position.set(request.targetX, request.targetY);
  sprite.scale.set(0.8);
  container.addChild(sprite);

  const dx = request.targetX - request.startX;
  const dy = request.targetY - request.startY;
  sprite.rotation = Math.atan2(dy, dx);

  let frame = 0;
  const totalFrames = 12;

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    // Quick expand and fade
    sprite.scale.set(0.8 + progress * 0.5);
    sprite.alpha = 1 - progress;

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(sprite);
      sprite.destroy();
      request.onComplete?.();
    }
  };

  requestAnimationFrame(animate);
}

/**
 * Render impact effect at position
 */
export function renderImpactEffect(
  container: PIXI.Container,
  effectKey: string,
  x: number,
  y: number,
  onComplete?: () => void
): boolean {
  const effect = effectCache.get(effectKey);
  if (!effect || effect.textures.length === 0) {
    return false;
  }

  const sprite = createAnimatedSprite(effect, 0.35);
  sprite.position.set(x, y);
  sprite.scale.set(0.5);
  container.addChild(sprite);

  let frame = 0;
  const totalFrames = 15;

  const animate = () => {
    frame++;
    const progress = frame / totalFrames;

    // Expand and fade
    sprite.scale.set(0.5 + progress * 1);
    sprite.alpha = 1 - progress;

    if (frame < totalFrames) {
      requestAnimationFrame(animate);
    } else {
      container.removeChild(sprite);
      sprite.destroy();
      onComplete?.();
    }
  };

  requestAnimationFrame(animate);
  return true;
}
